import { useState, useMemo, useEffect } from 'react';
import {
  Users, UserCheck, ShieldCheck, Monitor, Search, Download, Plus, ChevronDown, MoreHorizontal, RefreshCw, ShieldOff,
  ShieldAlert, ArrowUpDown, KeyRound, UserX, Trash2, Pencil, Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import UserDetailPanel from './UserDetailPanel';
import { Card, EmptyState, Pagination, SectionTitle, UserAvatar, StatusDot, displayName, formatRelative, selectClass } from './common';

const PER_PAGE = 10;
const FILTERS = [
  { id: 'all', label: 'Tüm durumlar' },
  { id: 'active', label: 'Aktif' },
  { id: 'suspended', label: 'Askıda' },
  { id: '2fa', label: '2FA açık' },
  { id: 'no2fa', label: '2FA kapalı' },
  { id: 'ip', label: 'IP kısıtlı' },
  { id: 'bulk', label: 'Toplu mail açık' },
];
const SORTS = [
  { id: 'created', label: 'Kayıt tarihi (yeni → eski)' },
  { id: 'login', label: 'Son giriş (yeni → eski)' },
  { id: 'name', label: 'Ada göre (A → Z)' },
];

const matchesFilter = (user, filter) => {
  switch (filter) {
    case 'active': return user.is_active;
    case 'suspended': return !user.is_active;
    case '2fa': return user.two_fa_enabled;
    case 'no2fa': return !user.two_fa_enabled;
    case 'ip': return user.ip_restriction_enabled;
    case 'bulk': return user.bulk_mail_enabled;
    default: return true;
  }
};

const csvCell = (value) => {
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`; // tablo programlarında formül çalıştırmasın
  return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const exportCsv = (rows) => {
  const header = ['E-posta', 'Ad soyad', 'Durum', '2FA', 'IP kısıtı', 'Toplu mail', 'Son giriş', 'Kayıt tarihi'];
  const lines = rows.map((u) => [
    u.email, u.display_name || '', u.is_active ? 'Aktif' : 'Askıda', u.two_fa_enabled ? 'Evet' : 'Hayır',
    u.ip_restriction_enabled ? 'Evet' : 'Hayır', u.bulk_mail_enabled ? 'Açık' : 'Kapalı',
    u.last_login || '', u.created_at || '',
  ].map(csvCell).join(','));
  const blob = new Blob([`﻿${[header.map(csvCell).join(','), ...lines].join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `kullanicilar-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const HealthItem = ({ ok, loading, label }) => (
  <span className="inline-flex items-center gap-2 text-sm">
    <span className={`h-2.5 w-2.5 rounded-full ${loading ? 'bg-muted-foreground/50' : ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
    {label}
  </span>
);

const StatCard = ({ icon: Icon, tone, value, label, badge }) => (
  <div className="flex items-center gap-4 px-5 py-4">
    <span className={`h-12 w-12 rounded-xl flex items-center justify-center ${tone}`}><Icon className="h-6 w-6" /></span>
    <div>
      <p className="text-2xl font-semibold leading-tight flex items-center gap-2">{value}{badge}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  </div>
);

export const UsersSection = ({
  users, stats, health, isCheckingHealth, onRefreshHealth, isLoading, searchQuery, onSearch,
  selectedId, onSelect, details, detailsLoading, onAddUser, actions,
}) => {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('created');
  const [page, setPage] = useState(1);
  const [checked, setChecked] = useState([]);

  const rows = useMemo(() => {
    const filtered = users.filter((u) => matchesFilter(u, filter));
    const time = (v) => (v ? new Date(v).getTime() : 0);
    const sorted = [...filtered];
    if (sort === 'login') sorted.sort((a, b) => time(b.last_login) - time(a.last_login));
    else if (sort === 'name') sorted.sort((a, b) => displayName(a).localeCompare(displayName(b), 'tr'));
    else sorted.sort((a, b) => time(b.created_at) - time(a.created_at));
    return sorted;
  }, [users, filter, sort]);

  useEffect(() => { setPage(1); }, [filter, sort, searchQuery]);
  const pageRows = rows.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const allChecked = pageRows.length > 0 && pageRows.every((u) => checked.includes(u.id));
  const toggleAll = () => setChecked((prev) => (allChecked ? prev.filter((id) => !pageRows.some((u) => u.id === id)) : [...new Set([...prev, ...pageRows.map((u) => u.id)])]));
  const toggleOne = (id) => setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const twoFaLow = stats && stats.total_users > 0 && stats.users_with_2fa / stats.total_users < 0.1;
  const status = (key) => health?.[key]?.status;
  const mailStatus = status('mail_service');
  const lastCheck = health?.checked_at ? new Date(health.checked_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '--:--';
  const panelOpen = Boolean(selectedId);

  return (
    <div className="p-4 sm:p-6 xl:p-8" data-testid="users-section">
      <SectionTitle title="Kullanıcı yönetimi" description="Hesapları, erişim izinlerini ve oturumları yönetin.">
        <Button variant="outline" onClick={() => exportCsv(checked.length ? users.filter((u) => checked.includes(u.id)) : rows)} data-testid="export-users">
          <Download className="h-4 w-4 mr-2" />{checked.length ? `Dışa aktar (${checked.length})` : 'Dışa aktar'}
        </Button>
        <Button onClick={onAddUser} data-testid="add-user-btn"><Plus className="h-4 w-4 mr-2" />Yeni kullanıcı</Button>
      </SectionTitle>

      <Card className="px-5 py-3 mb-4 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="health-bar">
        <HealthItem loading={!health} ok={status('backend') === 'online'} label={status('backend') === 'online' ? 'API çalışıyor' : 'API yanıt vermiyor'} />
        <HealthItem loading={!health} ok={status('database') === 'online'} label={status('database') === 'online' ? 'Veritabanı bağlı' : 'Veritabanı sorunu'} />
        <HealthItem loading={!health || mailStatus === 'unknown'} ok={mailStatus === 'online'} label={mailStatus === 'online' ? 'IMAP / SMTP aktif' : mailStatus === 'partial' ? 'IMAP / SMTP kısmen çalışıyor' : mailStatus === 'unknown' ? 'IMAP / SMTP kontrol edilemedi' : 'IMAP / SMTP sorunu'} />
        <button onClick={onRefreshHealth} className="ml-auto inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" aria-label="Durumu yenile">
          <RefreshCw className={`h-4 w-4 ${isCheckingHealth ? 'animate-spin' : ''}`} />Son kontrol: {lastCheck}
        </button>
      </Card>

      {stats && (
        <Card className="mb-4 grid grid-cols-2 xl:grid-cols-4 divide-y xl:divide-y-0 xl:divide-x divide-border" data-testid="stat-cards">
          <StatCard icon={Users} tone="bg-blue-100 text-blue-600 dark:bg-blue-500/20" value={stats.total_users} label="Toplam kullanıcı" />
          <StatCard icon={UserCheck} tone="bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20" value={stats.active_users} label="Aktif hesap" />
          <StatCard icon={ShieldCheck} tone="bg-violet-100 text-violet-600 dark:bg-violet-500/20" value={stats.users_with_2fa} label="2FA etkin"
            badge={twoFaLow ? <span className="rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 px-2 py-0.5 text-[11px] font-medium">Düşük kullanım</span> : null} />
          <StatCard icon={Monitor} tone="bg-cyan-100 text-cyan-600 dark:bg-cyan-500/20" value={stats.active_sessions} label="Aktif oturum" />
        </Card>
      )}

      <div className="flex flex-col xl:flex-row gap-4 items-start">
        <Card className="flex-1 min-w-0 w-full overflow-hidden">
          <div className="p-4 flex flex-wrap items-center gap-3 border-b border-border">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input value={searchQuery} onChange={(e) => onSearch(e.target.value)} placeholder="Kullanıcı ara..." aria-label="Kullanıcı ara" className={`${selectClass} w-full pl-9`} data-testid="users-search" />
            </div>
            <div className="relative">
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${selectClass} pr-8 appearance-none`} aria-label="Durum süzgeci" data-testid="users-filter">
                {FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-muted-foreground" />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="icon" className="h-10 w-10" aria-label="Sırala" data-testid="users-sort"><ArrowUpDown className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {SORTS.map((s) => <DropdownMenuItem key={s.id} onClick={() => setSort(s.id)}><span className={sort === s.id ? 'font-medium text-primary' : ''}>{s.label}</span></DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="w-12 pl-4 py-3"><input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Sayfadakilerin tümünü seç" className="h-4 w-4 rounded" /></th>
                  <th className="py-3 pr-3 font-medium">Kullanıcı</th>
                  <th className="py-3 pr-3 font-medium hidden sm:table-cell">Durum</th>
                  <th className="py-3 pr-3 font-medium hidden md:table-cell">Güvenlik</th>
                  <th className="py-3 pr-3 font-medium hidden lg:table-cell">Son giriş</th>
                  <th className="w-12" />
                </tr>
              </thead>
              <tbody>
                {isLoading && users.length === 0 && <tr><td colSpan={6}><div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></td></tr>}
                {pageRows.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => onSelect(u.id)}
                    className={`cursor-pointer border-b border-border/60 transition-colors ${selectedId === u.id ? 'bg-accent' : 'hover:bg-secondary/60'}`}
                    data-testid={`user-row-${u.id}`}
                  >
                    <td className={`pl-4 py-3 ${selectedId === u.id ? 'border-l-[3px] border-primary' : ''}`} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={checked.includes(u.id)} onChange={() => toggleOne(u.id)} aria-label={`${u.email} seç`} className="h-4 w-4 rounded" />
                    </td>
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <UserAvatar user={u} />
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{displayName(u)}</p>
                          <p className="text-muted-foreground truncate">{u.email}</p>
                          <p className="sm:hidden mt-0.5"><StatusDot active={u.is_active} label={u.is_active ? 'Aktif' : 'Askıda'} /></p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap hidden sm:table-cell"><StatusDot active={u.is_active} label={u.is_active ? 'Aktif' : 'Askıda'} /></td>
                    <td className="py-3 pr-3 hidden md:table-cell whitespace-nowrap">
                      {u.ip_restriction_enabled
                        ? <span className="inline-flex items-center gap-1.5 text-amber-600"><ShieldAlert className="h-4 w-4" />IP kısıtlı</span>
                        : u.two_fa_enabled
                          ? <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><ShieldCheck className="h-4 w-4" />2FA açık</span>
                          : <span className="inline-flex items-center gap-1.5 text-muted-foreground"><ShieldOff className="h-4 w-4" />2FA kapalı</span>}
                    </td>
                    <td className="py-3 pr-3 hidden lg:table-cell whitespace-nowrap text-muted-foreground">{formatRelative(u.last_login)}</td>
                    <td className="pr-3" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center" aria-label={`${u.email} işlemleri`}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => actions.onEditById(u.id)}><Pencil className="h-4 w-4 mr-2" />Düzenle</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => actions.onReset(u)}><KeyRound className="h-4 w-4 mr-2" />Şifre sıfırla</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => actions.onToggleStatus(u)}><UserX className="h-4 w-4 mr-2" />{u.is_active ? 'Hesabı askıya al' : 'Hesabı etkinleştir'}</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => actions.onDelete(u)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Sil</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!isLoading && rows.length === 0 && <EmptyState icon={Users} text="Kullanıcı bulunamadı" />}
          </div>
          <Pagination page={page} perPage={PER_PAGE} total={rows.length} onPage={setPage} noun="kullanıcı" />
        </Card>

        {panelOpen && (
          <Card className="fixed inset-0 z-40 rounded-none border-0 xl:rounded-xl xl:border xl:static xl:z-auto xl:w-[400px] xl:shrink-0 xl:sticky xl:top-4 xl:max-h-[calc(100vh_-_8rem)] overflow-hidden flex flex-col" data-testid="detail-card">
            <UserDetailPanel details={details} loading={detailsLoading} onClose={() => onSelect(null)} actions={actions} />
          </Card>
        )}
      </div>
    </div>
  );
};

export default UsersSection;
